import { AnalyzeScenarioResult } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningLambdaUtils';
import { ScenarioDateRow } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import { download, exportMultiSheetToExcel, Sheet } from '../../../utils/download-utils';

const scenarioDateRowToExportRow = (row: ScenarioDateRow): Record<string, unknown> => ({
  Date: row.date,
  Channel: row.channel,
  Platform: row.platform,
  Tactic: row.tactic,
  ...(row.modelLabel ? { Model: row.modelLabel } : {}),
  'Direct Spend': row.directSpend,
  'Direct Revenue': row.directRevenue,
  ...(row.indirectSpend != null ? { 'Indirect Spend': row.indirectSpend } : {}),
  ...(row.indirectRevenue != null ? { 'Indirect Revenue': row.indirectRevenue } : {}),
  ...(row.reattributedOutSpend != null
    ? { 'Reattributed Out Spend': row.reattributedOutSpend }
    : {}),
  ...(row.reattributedOutRevenue != null
    ? { 'Reattributed Out Revenue': row.reattributedOutRevenue }
    : {})
});

// Baseline is always present; Scenario is only included once a scenario has been analyzed.
export const buildScenarioPlanningExportSheets = (
  baselineData: ScenarioDateRow[],
  scenarioDataMap: Record<string, ScenarioDateRow> | undefined
): Sheet[] => {
  const sheets: Sheet[] = [
    { name: 'Baseline', data: baselineData.map(scenarioDateRowToExportRow) }
  ];
  if (scenarioDataMap) {
    sheets.push({
      name: 'Scenario',
      data: Object.values(scenarioDataMap).map(scenarioDateRowToExportRow)
    });
  }
  return sheets;
};

interface ExportScenarioPlanningDataArgs {
  baselineData: ScenarioDateRow[];
  scenarioDataMap: Record<string, ScenarioDateRow> | undefined;
  lastAnalyzeRawResult: AnalyzeScenarioResult | undefined;
  fileNamePrefix: string;
}

// Always exports a Baseline (+ Scenario, if analyzed) sheet as an xlsx workbook. Additionally,
// only when a scenario has actually been analyzed, downloads a second file: the raw
// (pre-processed) response from the last analyze-lambda call, for debugging/data-science use.
export const exportScenarioPlanningData = async ({
  baselineData,
  scenarioDataMap,
  lastAnalyzeRawResult,
  fileNamePrefix
}: ExportScenarioPlanningDataArgs): Promise<void> => {
  await exportMultiSheetToExcel(
    buildScenarioPlanningExportSheets(baselineData, scenarioDataMap),
    `${fileNamePrefix}_scenario_planning_data`
  );
  if (lastAnalyzeRawResult) {
    download(
      new Blob([JSON.stringify(lastAnalyzeRawResult, null, 2)], { type: 'application/json' }),
      `${fileNamePrefix}_scenario_planning_raw_analysis.json`,
      'application/json'
    );
  }
};
